'use client';

import { useAnalyticsPageView } from '@ria-client/segment/Analytics/AnalyticsPageView';
import { useStandardTrack } from '@ria-client/segment/hooks/useStandardTrack';
import type { Video } from '@ria-client/supergraph/structuredContent/hydration/WpVideo/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { getVideoImageSRC, getVideoImageSRCSet, getVideoUrl } from '../lib/video-utils';
import type { NodeProps } from '../types';
import { PlayMediaIcon } from './lib/play-media-icon';
import { VideoLinkedData } from './lib/video-linked-data';

interface VideoOptions {
  showPreview?: boolean;
  showRelatedVideos?: boolean;
  timeLength?: string;
  title?: string;
}

export type WpVideoProps = {
  options?: VideoOptions;
  service?: 'youtube';
  shouldRenderJsonLd?: boolean;
  video: Video;
};

export function WpVideo({ props }: NodeProps<WpVideoProps>) {
  const {
    options = { showPreview: true, showRelatedVideos: false },
    video,
    shouldRenderJsonLd = true,
  } = props;

  const [showPreview, setShowPreview] = useState(options.showPreview ?? true);
  const [isVisible, setIsVisible] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const eventFiredRef = useRef(false);
  const impressionId = useRef(crypto.randomUUID());
  const viewFiredRef = useRef(false);
  const track = useStandardTrack();
  const { pageViewReady } = useAnalyticsPageView();

  const videoId = video?.youtubeVideo?.id ?? '';
  const timeLength = /^\d{1,2}:\d{2}$/.test(options.timeLength ?? '')
    ? options.timeLength
    : undefined;

  const fireViewed = useCallback(() => {
    if (viewFiredRef.current || !pageViewReady) return;

    viewFiredRef.current = true;
    void track('Element Viewed', {
      additional_attributes: { content_id: videoId },
      entity_name: 'video',
      event_id: impressionId.current,
    });
  }, [pageViewReady, track, videoId]);

  const fireInteraction = useCallback(() => {
    fireViewed();
    if (!viewFiredRef.current) return;

    eventFiredRef.current = true;
    void track('Element Interacted', {
      additional_attributes: { content_id: videoId },
      entity_name: 'video',
      parent_event_id: impressionId.current,
    });
  }, [fireViewed, track, videoId]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (eventFiredRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
        return;
      }
      if (document.activeElement === iframeRef.current) fireInteraction();
    }, 250);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fireInteraction]);

  useEffect(() => {
    if (isVisible) fireViewed();
  }, [fireViewed, isVisible]);

  if (!video?.youtubeVideo) return null;

  return (
    <InView triggerOnce onChange={setIsVisible}>
      <div className="relative h-0 overflow-hidden pb-[56.25%]">
        {showPreview ? (
          <button
            aria-label="Play"
            className="absolute inset-0 flex cursor-pointer items-center justify-center overflow-hidden border-0 bg-transparent p-0"
            onClick={() => {
              setShowPreview(false);
              fireInteraction();
            }}
            type="button"
          >
            <span className="z-10 flex size-[70px] items-center justify-center rounded-full bg-background/90 sm:size-24">
              <PlayMediaIcon className="ml-1 mt-[7px] text-[2.75rem] text-secondary-foreground sm:mt-[2px] sm:text-[3.5rem]" />
            </span>
            {timeLength && (
              <span className="typography-body absolute bottom-2 right-2 z-10 bg-foreground px-4 py-2 text-background sm:bottom-3 sm:right-3">
                {timeLength}
              </span>
            )}
            {/* Native img preserves the YouTube thumbnail srcSet. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt="Video preview image"
              className="size-full object-cover"
              fetchPriority="high"
              src={getVideoImageSRC(video)}
              srcSet={getVideoImageSRCSet(video)}
            />
          </button>
        ) : (
          <iframe
            allow="autoplay"
            allowFullScreen
            className="absolute inset-0 size-full border-0"
            ref={iframeRef}
            src={getVideoUrl(videoId, options)}
            title={video.youtubeVideo.snippet.title}
          />
        )}
        {shouldRenderJsonLd && <VideoLinkedData id="video-linked-data" video={video} />}
      </div>
    </InView>
  );
}
